import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check } from "lucide-react";
import { api } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch {
        setState("error");
        return;
      }
      if (tries.current >= 20) {
        setState("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      stop = true;
    };
  }, [sessionId]);

  return (
    <main data-testid="payment-success-page" className="min-h-screen bg-[#060b14] text-white flex items-center px-6">
      <div className="max-w-xl mx-auto">
        {state === "checking" && (
          <>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#2a5fdb]">
              Checking payment…
            </p>
            <h1 className="mt-4 font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tight">
              One sec.
            </h1>
            <p className="mt-4 text-white/60">Confirming your payment with Stripe.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="w-14 h-14 border border-[#2a5fdb] flex items-center justify-center mb-8">
              <Check className="text-[#2a5fdb]" />
            </div>
            <h1 data-testid="payment-paid" className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tight">
              Payment <span className="text-[#2a5fdb]">received.</span>
            </h1>
            <p className="mt-4 text-white/60 leading-relaxed">
              You're all set — the session is locked in and paid. See you on court.
            </p>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <h1 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tight">
              Still <span className="text-[#2a5fdb]">processing.</span>
            </h1>
            <p className="mt-4 text-white/60 leading-relaxed">
              We couldn't confirm the payment just yet. If you completed
              checkout, it will land shortly — or text Owen on 0458 792 779 and
              he'll sort it.
            </p>
          </>
        )}
        <Link to="/" data-testid="payment-home-link" className="btn-sharp mt-10 inline-flex">
          Back to site <span aria-hidden>→</span>
        </Link>
      </div>
    </main>
  );
}
